import { useContext } from 'react';
import * as R from 'ramda';

// eslint-disable-next-line import/no-cycle
import { AuthContext } from 'authentication/AuthContext';

import { userRoles } from 'utils/currentUserRoles';
import { isAuthenticated } from 'utils/authService';

// This is a simple implementation for Roles Based Access Control (RBAC)
// If we add any new affiliate permissions or user groups
// then we need to make updates to this file
const useUserRolePermissions = () => {
  const { currentAffiliate, affiliates } = useContext(AuthContext);
  const { profile } = isAuthenticated();
  const hasMultipleRoles = Array.isArray(profile?.groups);

  // This goes against what we should be doing for groups/permissions but
  // here is a small run down in case you need it:
  // Roles should be general control for views, however in this application
  // currently Roles are based solely on the current selected affiliate from
  // the user (look to fix in future)
  // Permissions should be more granular, like show a button or not, however
  // in this current application Permissions are based on the user regardless
  // of which affiliate the user is portraying in the application (look to fix in future)

  const getPermissions = str =>
    hasMultipleRoles ? profile?.groups.some(group => group === str) : profile?.groups === str;

  // This is our affiliate groups based on the current affiliate selected
  // Roles
  const isShipper = R.any(R.o(R.any(R.equals(true)), R.props(['shipper'])), affiliates || []);
  const isSupplier = R.any(R.o(R.any(R.equals(true)), R.props(['supplier'])), affiliates || []);
  const isInventoryOwner = R.any(
    R.o(R.any(R.equals(true)), R.props(['inventoryOwner'])),
    affiliates || []
  );
  const isRefiner = R.any(R.o(R.any(R.equals(true)), R.props(['refiner'])), affiliates || []);
  const isSubscriber = R.any(
    R.o(R.any(R.equals(true)), R.props(['hasActiveSubscription'])),
    affiliates || []
  );
  const isCarrier = R.any(R.o(R.any(R.equals(true)), R.props(['carrier'])), affiliates || []);

  // This is our user permissions
  // Permissions
  const isDispatchingView = getPermissions(userRoles.DISPATCHING_VIEW);
  const isDispatchingManage = getPermissions(userRoles.DISPATCHING_MANAGE);
  const isProductOwnerView = getPermissions(userRoles.PRODUCT_OWNERSHIP_VIEW);
  const isProductOwnerManage = getPermissions(userRoles.PRODUCT_OWNERSHIP_MANAGE);
  const isProductLoadingView = getPermissions(userRoles.PRODUCT_LOADING_VIEW);
  const isProductLoadingManage = getPermissions(userRoles.PRODUCT_LOADING_MANAGE);
  const isProductShippingView = getPermissions(userRoles.PRODUCT_SHIPPING_VIEW);
  const isProductShippingManage = getPermissions(userRoles.PRODUCT_SHIPPING_MANAGE);
  const isAdmin = getPermissions(userRoles.ADMIN);
  const isUser = getPermissions(userRoles.USER);

  return {
    currentAffiliate,
    isAdmin,
    isCarrier,
    isDispatchingManage,
    isDispatchingView,
    isInventoryOwner,
    isProductLoadingManage,
    isProductLoadingView,
    isProductOwnerManage,
    isProductOwnerView,
    isProductShippingManage,
    isProductShippingView,
    isRefiner,
    isShipper,
    isSubscriber,
    isSupplier,
    isUser,
  };
};

export default useUserRolePermissions;
